/**
 * Графики финансового блока.
 *
 * Вёрстка — HTML, а не SVG: полосы и столбцы меняют только `transform`
 * (scaleX / scaleY от оси), поэтому рост при первом показе и перетекание
 * при смене фильтра делает CSS-переход, без пересчёта геометрии в JS.
 * Подписи стоят в отдельной колонке и не масштабируются вместе с полосой.
 *
 * Форма по задаче данных:
 *   сравнение по категориям   → горизонтальные полосы (подписи длинные)
 *   динамика по месяцам       → столбцы, ось от нуля
 *   поступления и списания    → столбцы вверх и вниз от нуля, чистый поток точкой
 *   структура долга по срокам → одна полоса из сегментов, порядковая шкала одного оттенка
 *
 * Значения всегда подписаны текстом: цвет различает ряды, но не несёт число.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react'
import { durationToken, easeEnter, prefersReducedMotion, useEntered, useTweenedNumber } from './motion'
import { formatMoneyShort } from '../format'

export interface Series {
  key: string
  label: string
  /** CSS-цвет из токенов: var(--series-1) и т. п. */
  color: string
}

// --- Число, докручивающееся к новому значению ------------------------------

export function TweenText({
  value,
  format,
  memoryKey,
}: {
  value: number | null
  format: (value: number) => string
  memoryKey?: string
}) {
  const shown = useTweenedNumber(value, memoryKey)
  return <>{shown === null ? '—' : format(shown)}</>
}

// --- Горизонтальные полосы --------------------------------------------------

export interface BarRow {
  key: string
  label: string
  sub?: ReactNode
  values: Array<number | null>
  format?: (value: number, seriesIndex: number) => string
  /** Дополнительная колонка справа — доля, изменение. */
  aside?: ReactNode
  /** Мини-тренд за 12 месяцев рядом с названием; null — месяц без данных. */
  trend?: Array<number | null>
}

export function BarRows({
  rows,
  series,
  max,
  reference,
  onSelect,
  selectedKey,
  valueWidth = 92,
  ariaLabel,
}: {
  rows: BarRow[]
  series: Series[]
  max?: number
  /** Вертикальная отметка, например средняя маржа по компании. */
  reference?: { value: number; label: string }
  onSelect?: (row: BarRow, element: HTMLElement) => void
  selectedKey?: string | null
  valueWidth?: number
  ariaLabel: string
}) {
  const entered = useEntered()
  const scaleMax =
    max ?? Math.max(1, ...rows.flatMap((row) => row.values.map((value) => Math.abs(value ?? 0))))
  const hasAside = rows.some((row) => row.aside !== undefined)
  const listRef = useFlipRows(rows.map((row) => row.key).join('|'))

  return (
    <div
      ref={listRef}
      className={`bars${onSelect ? ' bars--interactive' : ''}${series.length === 1 ? ' bars--single' : ''}`}
      style={{ '--value-width': `${valueWidth}px`, '--aside-width': hasAside ? '78px' : '0px' } as CSSProperties}
      role="list"
      aria-label={ariaLabel}
    >
      {reference && (
        <div className="bars__reference-row" aria-hidden="true">
          <span className="bars__reference-spacer" />
          <div className="bars__reference-track">
            <div
              className="bars__reference"
              style={{ transform: `translateX(${entered ? (reference.value / scaleMax) * 100 : 0}%)` }}
            >
              <span className="bars__reference-label">{reference.label}</span>
            </div>
          </div>
          <span />
          <span />
        </div>
      )}
      {rows.map((row, index) => {
        const content = (
          <>
            <span className="bars__label">
              <span className="bars__name-row">
                <span className="bars__name">{row.label}</span>
                {row.trend && <MiniLine values={row.trend} label={`${row.label}: 12 месяцев`} />}
              </span>
              {row.sub && <span className="bars__sub">{row.sub}</span>}
            </span>
            <span className="bars__track">
              {series.map((item, seriesIndex) => {
                const value = row.values[seriesIndex] ?? 0
                const scale = entered ? Math.min(1, Math.abs(value) / scaleMax) : 0
                return (
                  <span
                    key={item.key}
                    className={`bars__bar${value < 0 ? ' bars__bar--negative' : ''}`}
                    style={{
                      transform: `scaleX(${scale})`,
                      background: value < 0 ? undefined : item.color,
                      transitionDelay: entered ? undefined : `calc(${Math.min(index, 7)} * var(--stagger))`,
                    }}
                  />
                )
              })}
              {reference && (
                <span
                  className="bars__reference-line"
                  style={{ transform: `translateX(${entered ? (reference.value / scaleMax) * 100 : 0}%)` }}
                  aria-hidden="true"
                />
              )}
            </span>
            <span className="bars__values">
              {series.map((item, seriesIndex) => (
                <span key={item.key} className="bars__value mono">
                  <TweenText
                    value={row.values[seriesIndex] ?? null}
                    format={(value) => (row.format ? row.format(value, seriesIndex) : formatMoneyShort(value))}
                  />
                </span>
              ))}
            </span>
            <span className="bars__aside">{row.aside}</span>
          </>
        )

        const style = { '--i': Math.min(index, 7) } as CSSProperties
        const selected = selectedKey === row.key

        return onSelect ? (
          <button
            key={row.key}
            type="button"
            role="listitem"
            data-flip={row.key}
            className={`bars__row cascade${selected ? ' bars__row--selected' : ''}`}
            style={style}
            onClick={(event) => onSelect(row, event.currentTarget)}
            aria-label={`${row.label}: подробнее`}
          >
            {content}
          </button>
        ) : (
          <div key={row.key} role="listitem" data-flip={row.key} className="bars__row cascade" style={style}>
            {content}
          </div>
        )
      })}
    </div>
  )
}

/**
 * Перестановка строк при смене периода (FLIP): строка, сменившая место,
 * едет от прежней позиции к новой за --dur-chart, а не телепортируется.
 * Новые строки появляются своим каскадом, прежние его не повторяют.
 */
function useFlipRows(order: string) {
  const ref = useRef<HTMLDivElement>(null)
  const tops = useRef(new Map<string, number>())
  useLayoutEffect(() => {
    const node = ref.current
    if (!node) return
    const rows = [...node.querySelectorAll<HTMLElement>('[data-flip]')]
    const next = new Map(rows.map((row) => [row.dataset.flip!, row.offsetTop] as const))
    if (!prefersReducedMotion()) {
      for (const row of rows) {
        const before = tops.current.get(row.dataset.flip!)
        const after = next.get(row.dataset.flip!)!
        if (before === undefined || before === after) continue
        row.style.transition = 'none'
        row.style.transform = `translateY(${before - after}px)`
        row.getBoundingClientRect()
        row.style.transition = `transform var(--dur-chart) var(--ease-enter)`
        row.style.transform = ''
        const clear = () => {
          row.style.transition = ''
          row.removeEventListener('transitionend', clear)
        }
        row.addEventListener('transitionend', clear)
      }
    }
    tops.current = next
  }, [order])
  return ref
}

// --- Столбцы по месяцам -----------------------------------------------------

export interface ColumnPoint {
  key: string
  /** Короткая подпись под столбцом: «сен». */
  label: string
  /** Полная подпись в считывателе: «сентябрь 2026». */
  title: string
  values: Array<number | null>
  /** Для расходящихся столбцов — чистый поток точкой. */
  net?: number | null
  /** Месяц выбранного периода — подсвечен. */
  highlight?: boolean
  /**
   * Месяц не загружен из 1С или закрыт правами. Столбца нет вовсе:
   * пустой месяц рисуется пунктирной меткой, а не нулём.
   */
  missing?: 'nodata' | 'closed'
}

const MISSING_TEXT = { nodata: 'нет данных', closed: 'закрыто правами' } as const

export function ColumnChart({
  points,
  series,
  diverging = false,
  netLabel,
  height = 200,
  format = formatMoneyShort,
  ariaLabel,
  emptyText = 'Нет данных за эти месяцы',
  summaryTitle,
  fill,
}: {
  points: ColumnPoint[]
  series: Series[]
  /** Первый ряд вверх от нуля, второй — вниз. */
  diverging?: boolean
  netLabel?: string
  height?: number
  format?: (value: number) => string
  ariaLabel: string
  emptyText?: string
  /**
   * Подпись итога, если подсвечено несколько месяцев (квартал, год):
   * без наведения считыватель показывает сумму выбранного периода,
   * а не первый его месяц.
   */
  summaryTitle?: string
  /**
   * Высота графика по месту: блок занимает остаток колонки, а высота
   * поля графика — от `min` до `max`. Без этого колонки обзора кончались
   * на разной высоте. `height` тогда не используется.
   */
  fill?: { min: number; max: number }
}) {
  const entered = useEntered()
  const fitted = useFillHeight(fill)
  if (fill) height = fitted.height
  const lastIndex = points.length - 1
  const highlightedIndexes = points.map((point, index) => (point.highlight ? index : -1)).filter((index) => index >= 0)
  const highlighted = highlightedIndexes[0] ?? -1
  const defaultIndex = highlighted >= 0 ? highlighted : lastIndex
  const [active, setActive] = useState<number | null>(null)
  const shown = active ?? defaultIndex
  const plotRef = useRef<HTMLDivElement>(null)
  const summary = active === null && highlightedIndexes.length > 1

  useEffect(() => setActive(null), [points.length])

  const max = useMemo(
    () => Math.max(1, ...points.flatMap((point) => point.values.map((value) => Math.abs(value ?? 0)))),
    [points],
  )

  const hasData = points.some((point) => !point.missing && point.values.some((value) => (value ?? 0) !== 0))
  if (!hasData) return <div className="empty small">{emptyText}</div>

  const half = diverging ? height / 2 : height
  const point = points[shown]

  // Итог подсвеченного периода: суммы по загруженным месяцам; если все
  // месяцы периода пустые — «нет данных», а не «0 · 0».
  const summaryPoints = highlightedIndexes.map((index) => points[index]!).filter((item) => !item.missing)
  const readout = summary
    ? {
        title: summaryTitle ?? `${points[highlightedIndexes[0]!]!.label}–${points[highlightedIndexes[highlightedIndexes.length - 1]!]!.label}`,
        missing: summaryPoints.length === 0 ? ('nodata' as const) : undefined,
        values: series.map((_, index) => summaryPoints.reduce((sum, item) => sum + (item.values[index] ?? 0), 0)),
        net: summaryPoints.some((item) => item.net !== undefined && item.net !== null)
          ? summaryPoints.reduce((sum, item) => sum + (item.net ?? 0), 0)
          : null,
      }
    : { title: point?.title, missing: point?.missing, values: point?.values ?? [], net: point?.net ?? null }

  const onKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') setActive(Math.max(0, shown - 1))
    else if (event.key === 'ArrowRight') setActive(Math.min(lastIndex, shown + 1))
    else return
    event.preventDefault()
  }

  const ticks = diverging ? [max, 0, -max] : [max, max / 2, 0]

  const chart = (
    <div className="columns" ref={fill ? fitted.innerRef : undefined}>
      {/* Считыватель над графиком вместо плавающей подсказки: на телефоне
          нечем наводить, а касание столбца просто выбирает месяц. */}
      <div className="columns__readout" aria-live="polite">
        <span className="columns__readout-title">{readout.title}</span>
        {readout.missing ? (
          <span className="columns__readout-item muted">{MISSING_TEXT[readout.missing]}</span>
        ) : (
          <>
            {series.map((item, index) => (
              <span key={item.key} className="columns__readout-item">
                <span className="swatch" style={{ background: item.color }} aria-hidden="true" />
                <span className="muted">{item.label}</span>
                <span className="strong mono">
                  <TweenText value={readout.values[index] ?? null} format={format} />
                </span>
              </span>
            ))}
            {diverging && netLabel && (
              <span className="columns__readout-item">
                <span className="swatch swatch--dot" aria-hidden="true" />
                <span className="muted">{netLabel}</span>
                <span className="strong mono">
                  <TweenText value={readout.net} format={format} />
                </span>
              </span>
            )}
          </>
        )}
      </div>

      <div className="columns__body">
        <div className="columns__axis" style={{ height }} aria-hidden="true">
          {ticks.map((tick, index) => (
            <span
              key={index}
              className="columns__tick mono"
              style={{ transform: `translateY(${(index / (ticks.length - 1)) * height}px) translateY(-50%)` }}
            >
              {tick === 0 ? '0' : format(tick)}
            </span>
          ))}
        </div>

        <div
          ref={plotRef}
          className="columns__plot"
          style={{ height }}
          role="img"
          aria-label={ariaLabel}
          tabIndex={0}
          onKeyDown={onKey}
          onMouseLeave={() => setActive(null)}
        >
          <div className="columns__grid" aria-hidden="true">
            {ticks.map((_, index) => (
              <span
                key={index}
                className={`columns__gridline${ticks[index] === 0 ? ' columns__gridline--zero' : ''}`}
                style={{ transform: `translateY(${(index / (ticks.length - 1)) * height}px)` }}
              />
            ))}
          </div>

          {points.map((item, index) => (
            <div
              key={item.key}
              className={`columns__slot${index === shown && !summary ? ' columns__slot--active' : ''}${item.highlight ? ' columns__slot--highlight' : ''}${
                item.missing ? ` columns__slot--missing` : ''
              }`}
              onMouseEnter={() => setActive(index)}
              onClick={() => setActive(index)}
            >
              {item.missing && (
                <span className="columns__missing" style={{ top: diverging ? half - 6 : undefined }} aria-hidden="true" />
              )}
              <div className={`columns__group${diverging ? ' columns__group--diverging' : ''}`}>
                {series.map((serie, seriesIndex) => {
                  const value = item.missing ? 0 : (item.values[seriesIndex] ?? 0)
                  const scale = entered ? Math.min(1, Math.abs(value) / max) : 0
                  const down = diverging && seriesIndex === 1
                  return (
                    <span
                      key={serie.key}
                      className={`columns__bar${down ? ' columns__bar--down' : ''}${value < 0 && !diverging ? ' columns__bar--negative' : ''}`}
                      style={{
                        height: half,
                        top: down ? half : diverging ? 0 : undefined,
                        bottom: diverging ? undefined : 0,
                        transform: `scaleY(${scale})`,
                        background: value < 0 && !diverging ? undefined : serie.color,
                        transitionDelay: entered ? undefined : `calc(${Math.min(index, 7)} * var(--stagger))`,
                      }}
                    />
                  )
                })}
              </div>
              {diverging && !item.missing && item.net !== undefined && item.net !== null && (
                <span
                  className="columns__net"
                  style={{
                    transform: `translateY(${entered ? half - (item.net / max) * half : half}px)`,
                    opacity: entered ? 1 : 0,
                  }}
                  aria-hidden="true"
                />
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="columns__labels" aria-hidden="true">
        {points.map((item, index) => (
          <span
            key={item.key}
            className={`columns__label${index === shown && !summary ? ' columns__label--active' : ''}${item.highlight && summary ? ' columns__label--active' : ''}${
              item.missing ? ' columns__label--missing' : ''
            }${(lastIndex - index) % 2 === 1 ? ' columns__label--odd' : ''}${(lastIndex - index) % 3 !== 0 ? ' columns__label--third-off' : ''}`}
          >
            {item.label}
          </span>
        ))}
      </div>

    </div>
  )

  if (!fill) return chart
  return (
    <div className="columns-fill" ref={fitted.boxRef} style={{ minHeight: fill.min + fitted.chrome }}>
      {chart}
    </div>
  )
}

/**
 * Высота поля графика по свободному месту. Сам график стоит в блоке
 * абсолютно и в высоту колонки не входит: иначе колонка подстраивалась бы
 * под график, а график под колонку. «Обвязка» (считыватель и подписи
 * месяцев) меряется: на узкой ширине считыватель переносится в две строки.
 */
function useFillHeight(fill?: { min: number; max: number }) {
  const boxRef = useRef<HTMLDivElement>(null)
  const innerRef = useRef<HTMLDivElement>(null)
  const [box, setBox] = useState(0)
  const [chrome, setChrome] = useState(60)
  const min = fill?.min ?? 0
  const max = fill?.max ?? 0
  const height = Math.round(Math.max(min, Math.min(max, box - chrome)))

  useLayoutEffect(() => {
    if (!fill) return
    const measure = () => {
      const outer = boxRef.current
      const inner = innerRef.current
      if (!outer || !inner) return
      const plot = inner.querySelector<HTMLElement>('.columns__plot')
      if (plot) setChrome(Math.round(inner.offsetHeight - plot.offsetHeight))
      setBox(outer.clientHeight)
    }
    measure()
    const observer = new ResizeObserver(measure)
    if (boxRef.current) observer.observe(boxRef.current)
    if (innerRef.current) observer.observe(innerRef.current)
    return () => observer.disconnect()
  }, [Boolean(fill)])

  return { boxRef, innerRef, chrome, height }
}

/**
 * Блок на свободное место в конце колонки: сколько строк высоты `row`
 * в нём помещается. Содержимое стоит абсолютно и высоту колонки не задаёт.
 */
export function FillRows({ row, reserve = 0, children }: { row: number; reserve?: number; children: (count: number) => ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)
  const [count, setCount] = useState(0)
  useLayoutEffect(() => {
    const node = ref.current
    if (!node) return
    const measure = () => setCount(Math.max(0, Math.floor((node.clientHeight - reserve) / row)))
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [row, reserve])
  return (
    <div className="fill-rows" ref={ref}>
      <div className="fill-rows__inner">{children(count)}</div>
    </div>
  )
}

// --- Структура долга по срокам ---------------------------------------------

export interface Segment {
  key: string
  label: string
  value: number
  color: string
}

export function SegmentBar({ segments, ariaLabel }: { segments: Segment[]; ariaLabel: string }) {
  const entered = useEntered()
  const total = segments.reduce((sum, item) => sum + Math.max(0, item.value), 0)
  let offset = 0

  return (
    <div className="segments" role="img" aria-label={ariaLabel}>
      {segments.map((item) => {
        const share = total > 0 ? Math.max(0, item.value) / total : 0
        const start = offset
        offset += share
        return (
          <span
            key={item.key}
            className="segments__part"
            style={{
              background: item.color,
              transform: `translateX(${(entered ? start : 0) * 100}%) scaleX(${entered ? share : 0})`,
            }}
          />
        )
      })}
      {/* Зазор цветом поверхности на каждой границе: соседние сегменты
          одного оттенка различимы и без цвета. */}
      {segments.slice(0, -1).map((item, index) => {
        const boundary = segments.slice(0, index + 1).reduce((sum, part) => sum + Math.max(0, part.value), 0)
        return (
          <span
            key={`gap-${item.key}`}
            className="segments__gap"
            style={{ transform: `translateX(${entered && total > 0 ? (boundary / total) * 100 : 0}%)` }}
          />
        )
      })}
    </div>
  )
}

// --- Мини-тренд в карточке --------------------------------------------------

/** Линия-тренд в строке полос: без осей и точки, только форма за 12 месяцев. */
function MiniLine({ values, label }: { values: Array<number | null>; label: string }) {
  const known = values.filter((value): value is number => value !== null)
  if (known.length < 3) return null
  const width = 44
  const height = 14
  const min = Math.min(...known)
  const max = Math.max(...known)
  const span = max - min || 1
  let d = ''
  let pen = false
  values.forEach((value, index) => {
    if (value === null) {
      pen = false
      return
    }
    const x = (index / (values.length - 1)) * width
    const y = height - 1 - ((value - min) / span) * (height - 2)
    d += `${pen ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)} `
    pen = true
  })
  return (
    <svg className="bars__trend" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={label}>
      <path d={d.trim()} fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

/**
 * Значения линии, перетекающие к новым: при смене периода или города
 * мини-тренд меняет форму, а не стирается и рисуется заново. Если
 * пропуски месяцев разные, перетекать нечему — новая форма встаёт сразу.
 */
function useTweenedSeries(values: Array<number | null>): Array<number | null> {
  const [shown, setShown] = useState(values)
  const current = useRef(values)
  const frame = useRef<number | null>(null)
  const signature = values.map((value) => (value === null ? 'n' : value)).join('|')

  useEffect(() => {
    const from = current.current
    const comparable =
      from.length === values.length && from.every((value, index) => (value === null) === (values[index] === null))
    if (!comparable || prefersReducedMotion()) {
      current.current = values
      setShown(values)
      return
    }
    const duration = durationToken('--dur-chart', 360)
    const started = performance.now()
    const step = (now: number) => {
      const progress = Math.min(1, (now - started) / duration)
      const eased = easeEnter(progress)
      const next = values.map((value, index) => (value === null ? null : from[index]! + (value - from[index]!) * eased))
      current.current = next
      setShown(next)
      if (progress < 1) frame.current = requestAnimationFrame(step)
    }
    frame.current = requestAnimationFrame(step)
    return () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current)
    }
  }, [signature])

  return shown
}

export function Sparkline({
  values: target,
  color = 'var(--series-1)',
  label,
  ghostIndex,
}: {
  values: Array<number | null>
  color?: string
  label: string
  /** Прошлый период: полый маркер на линии — видно, откуда пришли к текущему. */
  ghostIndex?: number
}) {
  const values = useTweenedSeries(target)
  const known = values.filter((value): value is number => value !== null)
  if (known.length < 2) return null
  const width = 120
  const height = 32
  const min = Math.min(0, ...known)
  const max = Math.max(...known, 1)
  const span = max - min || 1
  const x = (index: number) => (index / (values.length - 1)) * width
  const y = (value: number) => height - 2 - ((value - min) / span) * (height - 4)
  // Месяц без данных — разрыв линии, а не провал в ноль.
  let d = ''
  let pen = false
  values.forEach((value, index) => {
    if (value === null) {
      pen = false
      return
    }
    d += `${pen ? 'L' : 'M'}${x(index).toFixed(1)} ${y(value).toFixed(1)} `
    pen = true
  })
  let lastIndex = values.length - 1
  while (lastIndex > 0 && values[lastIndex] === null) lastIndex -= 1
  const last = values[lastIndex] ?? 0
  const trailing = lastIndex < values.length - 1
  // Точки — отрезки нулевой длины с круглым концом и толщиной в экранных
  // пикселях: линия сжимается по ширине карточки, а точки остаются круглыми.
  const dot = (cx: number, cy: number, size: number, stroke: string, className: string) => (
    <line className={className} x1={cx} y1={cy} x2={cx} y2={cy} stroke={stroke} strokeWidth={size} strokeLinecap="round" vectorEffect="non-scaling-stroke" />
  )

  return (
    <svg
      className="sparkline"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={trailing ? `${label}; последние месяцы не загружены` : label}
    >
      {/* Линия рисуется при первом показе; при новых данных её точки перетекают (useTweenedSeries). */}
      <path className="sparkline__line" d={d.trim()} pathLength={1} fill="none" stroke={color} strokeWidth={2} />
      {trailing && (
        <line className="sparkline__gap" x1={x(lastIndex)} x2={width} y1={height - 2} y2={height - 2} vectorEffect="non-scaling-stroke" />
      )}
      {ghostIndex !== undefined && ghostIndex >= 0 && ghostIndex < lastIndex && values[ghostIndex] !== null && values[ghostIndex] !== undefined && (
        <g className="sparkline__ghost">
          {dot(x(ghostIndex), y(values[ghostIndex]!), 6, color, '')}
          {dot(x(ghostIndex), y(values[ghostIndex]!), 3, 'var(--surface)', '')}
        </g>
      )}
      {dot(x(lastIndex), y(last), 5.5, color, 'sparkline__dot')}
    </svg>
  )
}

export function Legend({ series }: { series: Series[] }) {
  if (series.length < 2) return null
  return (
    <div className="chart-legend" style={{ padding: '0 0 12px' }}>
      {series.map((item) => (
        <span className="chart-legend__item" key={item.key}>
          <span className="chart-legend__swatch" style={{ background: item.color }} aria-hidden="true" />
          {item.label}
        </span>
      ))}
    </div>
  )
}
